"use strict";
// Copyright © Veeam Software Group GmbH
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.List = void 0;
const react_1 = __importStar(require("react"));
const fast_deep_equal_1 = __importDefault(require("fast-deep-equal"));
const Grid_1 = require("../Grid");
const types_1 = require("../Grid/types");
const helpers_1 = require("../helpers");
const defaultIconGetter_1 = require("./helpers/defaultIconGetter");
const defaultTextGetter_1 = require("./helpers/defaultTextGetter");
const getIndexData_1 = require("./helpers/getIndexData");
const SelectionMode_1 = require("./types/SelectionMode");
const iconTextCellRenderer_1 = require("./renderers/iconTextCellRenderer");
exports.List = (0, helpers_1.createUIElement)('List', (_a, ref) => {
    var { className, data, selected = [], valueGetter, iconGetter = defaultIconGetter_1.defaultIconGetter, textGetter = defaultTextGetter_1.defaultTextGetter, comparator = fast_deep_equal_1.default, columnWidth, disabled = false, isRowDisabled, selectionMode = SelectionMode_1.SELECTION_MODE.single, hasCheckbox = false, hasSelectionHeader = true, heightMode = types_1.GRID_HEIGHT_MODE.auto, onChange, onClick, onDoubleClick, texts = {
        empty: 'No data'
    }, cellRenderer, onMouseEnter, onMouseLeave, onKeyDown, onKeyUp, onKeyPress, style, ['data-selector-id']: initialDataSelectorId } = _a, rest = __rest(_a, ["className", "data", "selected", "valueGetter", "iconGetter", "textGetter", "comparator", "columnWidth", "disabled", "isRowDisabled", "selectionMode", "hasCheckbox", "hasSelectionHeader", "heightMode", "onChange", "onClick", "onDoubleClick", "texts", "cellRenderer", "onMouseEnter", "onMouseLeave", "onKeyDown", "onKeyUp", "onKeyPress", "style", 'data-selector-id']);
    const dataRef = (0, react_1.useRef)(data);
    dataRef.current = data;
    if (selectionMode === SelectionMode_1.SELECTION_MODE.single && selected.length > 1) {
        console.warn("The prop 'value' must be an array of length equal to 1 in 'List' with selectionMode = SELECTION_MODE.single.");
    }
    const getGridRowItem = (0, react_1.useCallback)((option) => ({
        value: valueGetter(option),
        icon: iconGetter(option),
        text: textGetter(option)
    }), [iconGetter, textGetter, valueGetter]);
    const gridData = dataRef.current.map(getGridRowItem);
    const gridSelected = (0, react_1.useMemo)(() => selected.map((value) => {
        const index = (0, getIndexData_1.getIndexData)(dataRef.current, value, valueGetter, comparator);
        return getGridRowItem(dataRef.current[index]);
    }), [comparator, getGridRowItem, selected, valueGetter]);
    const isGridRowDisabled = (0, react_1.useCallback)(({ rowIndex }) => {
        return !!(isRowDisabled === null || isRowDisabled === void 0 ? void 0 : isRowDisabled({
            rowData: dataRef.current[rowIndex],
            rowIndex
        }));
    }, [isRowDisabled]);
    const selectionChangeHandler = (0, react_1.useCallback)(({ selected }) => {
        onChange === null || onChange === void 0 ? void 0 : onChange(selected.map((selectedRowProp) => {
            return selectedRowProp.value;
        }));
    }, [onChange]);
    const rowClickHandler = (0, react_1.useCallback)(({ rowIndex }) => {
        onClick === null || onClick === void 0 ? void 0 : onClick({
            dataItem: dataRef.current[rowIndex],
            rowIndex
        });
    }, [onClick]);
    const rowDoubleClickHandler = (0, react_1.useCallback)(({ rowIndex }) => {
        onDoubleClick === null || onDoubleClick === void 0 ? void 0 : onDoubleClick({
            dataItem: dataRef.current[rowIndex],
            rowIndex
        });
    }, [onDoubleClick]);
    const selection = (0, react_1.useMemo)(() => ({
        multiple: selectionMode === SelectionMode_1.SELECTION_MODE.multi,
        checkbox: hasCheckbox,
        fullLength: gridData.length,
        field: 'value',
        selected: gridSelected,
        subheaderRenderer: hasSelectionHeader ? undefined : null
    }), [gridData.length, gridSelected, hasCheckbox, hasSelectionHeader, selectionMode]);
    const cell = (0, react_1.useMemo)(() => cellRenderer || iconTextCellRenderer_1.IconTextCellRenderer, [cellRenderer]);
    return (react_1.default.createElement(Grid_1.Grid, Object.assign({}, rest, { ref: ref, style: style, "data-selector-id": initialDataSelectorId, disabled: disabled, isRowDisabled: isGridRowDisabled, hideHeader: true, data: gridData, height: heightMode, onSelectionChange: selectionChangeHandler, onRowClick: rowClickHandler, onRowDoubleClick: onDoubleClick ? rowDoubleClickHandler : undefined, selection: selection, texts: texts, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, onKeyDown: onKeyDown, onKeyUp: onKeyUp, onKeyPress: onKeyPress, className: (0, helpers_1.cn)('vm-List', className) }), react_1.default.createElement(Grid_1.GridColumn, { width: columnWidth, cell: cell, field: 'text' })));
});
//# sourceMappingURL=List.js.map